import { Link, useLocation } from "react-router-dom";
import "./mobile-bottom-nav.css";
import { appUrl, env } from "@astro/config";

/*
 * Shared mobile bottom tab bar (visible under 1024px), used by every app.
 * One fixed set of tabs — the website's — so the bar is identical everywhere.
 */

type NavTabDestination = "website" | "shop" | "jyotish" | "education";

type NavTab = {
  key: "home" | "shop" | "consult" | "academy";
  label: string;
  to: NavTabDestination;
  paths: string[];
};

const NAV_TABS: NavTab[] = [
  {
    key: "home",
    label: "Home",
    to: "website",
    paths: ["M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8", "M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"],
  },
  {
    key: "shop",
    label: "Shop",
    to: "shop",
    paths: ["M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z", "M3 6h18", "M16 10a4 4 0 0 1-8 0"],
  },
  {
    key: "consult",
    label: "Consult",
    to: "jyotish",
    paths: ["M7.9 20A9 9 0 1 0 4 16.1L2 22Z"],
  },
  {
    key: "academy",
    label: "Academy",
    to: "education",
    paths: ["M21.42 10.922a1 1 0 0 0-.019-1.838L12.83 5.18a2 2 0 0 0-1.66 0L2.6 9.08a1 1 0 0 0 0 1.832l8.57 3.908a2 2 0 0 0 1.66 0z", "M22 10v6", "M6 12.5V16a6 3 0 0 0 12 0v-3.5"],
  },
];

const CENTRE_TAB = {
  key: "horoscope",
  label: "Horoscope",
  to: "/horoscope",
  path: "M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z",
};

function TabIcon({ paths, size = 22, strokeWidth = 2 }: { paths: string[]; size?: number; strokeWidth?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {paths.map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}

export function MobileBottomNav() {
  const { pathname } = useLocation();
  // Which sibling app is rendering this bar — each app's .env sets
  // VITE_APP_NAME to its AppKey ("website" | "jyotish" | "shop" | "education").
  const currentApp = env.VITE_APP_NAME;
  const onHoroscopePage = currentApp === "website" && pathname.startsWith(CENTRE_TAB.to);

  // A tab is "on" when we're inside its app (the centre tab claims /horoscope).
  const isActive = (to: NavTabDestination) => to === currentApp && !onHoroscopePage;

  // Same-app tabs stay SPA navigation; cross-app tabs are full-page loads.
  const renderTab = (tab: NavTab, strokeWidth?: number) => {
    const inner = (
      <>
        <span className="mn-icon">
          <TabIcon paths={tab.paths} strokeWidth={strokeWidth} />
        </span>
        <span className="mn-label">{tab.label}</span>
      </>
    );
    const className = `mn-tab${isActive(tab.to) ? " mn-on" : ""}`;
    return tab.to === currentApp ? (
      <Link key={tab.key} to="/" title={tab.label} className={className}>
        {inner}
      </Link>
    ) : (
      <a key={tab.key} href={appUrl(tab.to)} title={tab.label} className={className}>
        {inner}
      </a>
    );
  };

  const centreInner = (
    <>
      <span className="mn-centre-hub">
        <span className="mn-centre-glow" aria-hidden="true" />
        <span className="mn-centre-ring" aria-hidden="true" />
        <span className="mn-centre-icon">
          <svg width="27" height="27" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
            <path d={CENTRE_TAB.path} />
          </svg>
        </span>
      </span>
      <span className="mn-centre-label">{CENTRE_TAB.label}</span>
    </>
  );

  return (
    <nav id="mn-nav" aria-label="Mobile navigation">
      <div className="mn-row">
        {NAV_TABS.slice(0, 2).map((tab) => renderTab(tab, tab.key === "home" ? 2.3 : 2))}

        {/* The horoscope page lives in the website app — SPA link there, full
            page load from every other app. */}
        {currentApp === "website" ? (
          <Link to={CENTRE_TAB.to} title={CENTRE_TAB.label} className="mn-centre">
            {centreInner}
          </Link>
        ) : (
          <a href={appUrl("website", CENTRE_TAB.to)} title={CENTRE_TAB.label} className="mn-centre">
            {centreInner}
          </a>
        )}

        {NAV_TABS.slice(2).map((tab) => renderTab(tab))}
      </div>
    </nav>
  );
}
